import type { ReactNode } from "react";
import {
  ActionIcon,
  Button,
  Checkbox,
  FileButton,
  Group,
  Paper,
  Progress,
  Select,
  Stack,
  Text,
} from "@mantine/core";
import { IconUpload, IconX } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import {
  HARDWARE_FAMILY_NAMES,
  type HardwareFamily,
  type ReleaseApkSummary,
  type StoredApk,
} from "types/headsetReleaseTypes";
import type { FamilyUploadState } from "./useFamilyUploads";

const APK_ACCEPT = ".apk,application/vnd.android.package-archive";

export const megabytes = (bytes: number) => `${Math.round(bytes / (1024 * 1024))} MB`;

export function isVersionMismatch(upload: FamilyUploadState | undefined, version?: string) {
  return !!upload?.inspection && !!version && upload.inspection.versionName !== version;
}

export function storedCopyOf(upload: FamilyUploadState | undefined, storedApks: StoredApk[]) {
  if (!upload?.inspection || upload.fromStorage) return undefined;
  const { versionName } = upload.inspection;
  return storedApks.find((stored) => stored.version === versionName);
}

interface FamilySlotProps {
  family: HardwareFamily;
  selected: boolean;
  onSelectedChange: (selected: boolean) => void;
  onFile: (file: File) => void;
  onRemoveUpload: () => void;
  onPickStored: (stored: StoredApk) => void;
  storedApks: StoredApk[];
  apk?: ReleaseApkSummary;
  upload?: FamilyUploadState;
  expectedVersion?: string;
  wrongFamilyAction?: ReactNode;
}

export function FamilySlot({
  family,
  selected,
  onSelectedChange,
  onFile,
  onRemoveUpload,
  onPickStored,
  storedApks,
  apk,
  upload,
  expectedVersion,
  wrongFamilyAction,
}: FamilySlotProps) {
  const { t } = useTranslation();
  const name = HARDWARE_FAMILY_NAMES[family];
  const acceptsFile = selected && !apk;
  const suggestedFamily = upload?.inspection?.suggestedFamily;
  const looksLikeOtherFamily = !!suggestedFamily && suggestedFamily !== family;
  const storedSameVersion = storedCopyOf(upload, storedApks);

  const pickFile = (file: File | null) => {
    if (file?.name.toLowerCase().endsWith(".apk")) onFile(file);
  };

  return (
    <Paper
      withBorder
      radius="md"
      p="md"
      onDragOver={(event) => acceptsFile && event.preventDefault()}
      onDrop={(event) => {
        if (!acceptsFile) return;
        event.preventDefault();
        pickFile(event.dataTransfer.files[0] ?? null);
      }}
      style={
        selected
          ? { borderColor: "var(--mantine-primary-color-filled)", borderWidth: 2 }
          : undefined
      }
    >
      <Stack gap="sm">
        <Group justify="space-between" wrap="nowrap">
          <Checkbox
            label={name}
            fw={700}
            checked={selected}
            onChange={(event) => onSelectedChange(event.currentTarget.checked)}
          />
          {upload && !upload.saved && (
            <ActionIcon
              variant="subtle"
              color="gray"
              aria-label={t("headsetReleases.removeFile", { name: upload.fileName })}
              onClick={onRemoveUpload}
            >
              <IconX size={16} />
            </ActionIcon>
          )}
        </Group>

        {apk ? (
          <>
            <Text size="sm" c="dimmed">
              {megabytes(apk.sizeBytes)}
            </Text>
            {selected && (
              <Text size="xs" fw={600} c="green">
                {t("headsetReleases.apkReady")}
              </Text>
            )}
          </>
        ) : upload ? (
          <>
            <Text size="sm" c="dimmed" ff="monospace" truncate>
              {upload.fileName} · {megabytes(upload.sizeBytes)}
            </Text>
            {upload.fromStorage && (
              <Text size="xs" c="dimmed">
                {t("headsetReleases.inStorage")}
              </Text>
            )}
            {upload.error ? (
              <Text size="xs" fw={600} c="red">
                {upload.error}
              </Text>
            ) : !upload.inspection ? (
              <Progress
                value={upload.fromStorage ? 100 : upload.progress * 100}
                animated={upload.fromStorage}
                size="sm"
                aria-label={upload.fileName}
              />
            ) : isVersionMismatch(upload, expectedVersion) ? (
              <Text size="xs" fw={600} c="red">
                {t("headsetReleases.versionMismatch", { version: expectedVersion })}
              </Text>
            ) : (
              <>
                <Text size="xs" fw={600} c="green">
                  {t("headsetReleases.verifiedVersion", {
                    version: upload.inspection.versionName,
                  })}
                </Text>
                {storedSameVersion && (
                  <Group gap="xs">
                    <Text size="xs" fw={600} c="orange">
                      {t("headsetReleases.alreadyInStorage", {
                        fileName: storedSameVersion.fileName,
                      })}
                    </Text>
                    <Button
                      size="compact-xs"
                      variant="light"
                      color="orange"
                      onClick={() => onPickStored(storedSameVersion)}
                    >
                      {t("headsetReleases.useStoredFile")}
                    </Button>
                  </Group>
                )}
                {looksLikeOtherFamily && (
                  <Group gap="xs">
                    <Text size="xs" fw={600} c="orange">
                      {t("headsetReleases.looksLikeFamily", {
                        family: HARDWARE_FAMILY_NAMES[suggestedFamily],
                      })}
                    </Text>
                    {wrongFamilyAction}
                  </Group>
                )}
              </>
            )}
          </>
        ) : (
          <Group gap="xs" wrap="nowrap">
            <FileButton onChange={pickFile} accept={APK_ACCEPT}>
              {(props) => (
                <Button
                  {...props}
                  variant="default"
                  size="xs"
                  leftSection={<IconUpload size={14} />}
                  disabled={!acceptsFile}
                  style={{ borderStyle: "dashed", flexShrink: 0 }}
                >
                  {t("headsetReleases.uploadFamilyApk", { family: name })}
                </Button>
              )}
            </FileButton>
            {storedApks.length > 0 && (
              <Select
                size="xs"
                placeholder={t("headsetReleases.pickStoredApk")}
                aria-label={t("headsetReleases.pickStoredApkFor", { family: name })}
                disabled={!acceptsFile}
                searchable
                value={null}
                data={storedApks.map((stored) => ({
                  value: stored.version,
                  label: `${stored.version} · ${megabytes(stored.sizeBytes)}`,
                }))}
                onChange={(version) => {
                  const stored = storedApks.find((candidate) => candidate.version === version);
                  if (stored) onPickStored(stored);
                }}
              />
            )}
          </Group>
        )}
      </Stack>
    </Paper>
  );
}
